import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';

import PageLayout from 'layouts/PageLayout';
import {
  getMoodleNonSynchronizedStatuses,
  startMoodleReconcileRun,
  getMoodleReconcileRun,
  getMoodleSyncHealthDashboard,
} from 'http/moodle';

const useStyles = makeStyles(theme => ({
  controlsWrap: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  controlsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  controlsGrid: {
    alignItems: 'flex-end',
    rowGap: theme.spacing(2),
    columnGap: theme.spacing(2),
  },
  controlsButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  section: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectionDivider: {
    margin: `${theme.spacing(2)}px 0`,
  },
  sectionHeader: {
    marginBottom: theme.spacing(1.5),
  },
  metricsGrid: {
    marginTop: theme.spacing(1),
  },
  metricCard: {
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.grey[300]}`,
  },
  metricValue: {
    fontSize: '1.35rem',
    fontWeight: 600,
  },
  reasonsWrap: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
  },
  reasonChipSecondary: {
    opacity: 0.8,
  },
  reasonChip: {
    cursor: 'pointer',
  },
  filterHint: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  activeFilterWrap: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  issuesHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1.5),
  },
  issuesActions: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  tableCellCompact: {
    whiteSpace: 'nowrap',
    verticalAlign: 'top',
  },
  tableCellWrap: {
    wordBreak: 'break-word',
    maxWidth: 280,
    verticalAlign: 'top',
  },
  stateLine: {
    marginBottom: theme.spacing(0.5),
  },
  errorText: {
    color: theme.palette.error.main,
  },
  helperText: {
    color: theme.palette.text.secondary,
  },
  healthAlertCritical: {
    backgroundColor: '#fce8e6',
    color: '#b3261e',
  },
  healthAlertWarning: {
    backgroundColor: '#fef7e0',
    color: '#8a5b00',
  },
  healthAlertWrap: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
  },
  healthTable: {
    marginTop: theme.spacing(1),
  },
}));

const REASON_ORDER = [
  'external_moodle_user',
  'archived_enrollment_history',
  'missing_user_link',
  'missing_course_link',
  'missing_enrollment',
  'state_mismatch',
  'missing_issueid',
  'unknown_status',
];
const INFO_ONLY_REASONS = ['external_moodle_user', 'archived_enrollment_history'];

const normalizeStateValue = value => {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  return value;
};

const getStateMismatchFields = (currentState, expectedState) => {
  if (!currentState || !expectedState) {
    return [];
  }

  const mismatches = [];
  if (
    normalizeStateValue(currentState.isPerformedWithSuccess) !==
    normalizeStateValue(expectedState.isPerformedWithSuccess)
  ) {
    mismatches.push('isPerformedWithSuccess');
  }
  if (normalizeStateValue(currentState.performedDate) !== normalizeStateValue(expectedState.performedDate)) {
    mismatches.push('performedDate');
  }
  if (normalizeStateValue(currentState.issueid) !== normalizeStateValue(expectedState.issueid)) {
    mismatches.push('issueid');
  }
  return mismatches;
};

const toDisplayState = state => {
  if (!state) {
    return '-';
  }
  return `success=${state.isPerformedWithSuccess}, performedDate=${state.performedDate || 'null'}, issueid=${
    state.issueid || 'null'
  }`;
};

const parseNonNegativeInt = rawValue => {
  const normalizedValue = rawValue === null || rawValue === undefined ? '' : String(rawValue).trim();
  if (!normalizedValue) {
    return { value: undefined };
  }

  const parsed = parseInt(normalizedValue, 10);
  if (isNaN(parsed) || parsed < 0) {
    return { error: true };
  }

  return { value: parsed };
};

const getApiErrorMessage = (payload, fallbackMessage) => {
  if (!payload) {
    return fallbackMessage;
  }

  if (Array.isArray(payload.message)) {
    return payload.message.join(', ');
  }

  if (typeof payload.message === 'string' && payload.message.trim()) {
    return payload.message;
  }

  if (typeof payload.error === 'string' && payload.error.trim()) {
    return payload.error;
  }

  return fallbackMessage;
};

const isApiSuccess = response => response && !response.error && !response.statusCode;
const isInformationalReason = reason => INFO_ONLY_REASONS.includes(reason);

const MoodleSync = () => {
  const classes = useStyles();
  const { t } = useTranslation('common');

  const [fromDateInput, setFromDateInput] = useState('0');
  const [limitInput, setLimitInput] = useState('200');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [lastAction, setLastAction] = useState('audit');
  const [selectedReason, setSelectedReason] = useState('');
  const [reconcileRun, setReconcileRun] = useState(null);
  const [isPollingRun, setIsPollingRun] = useState(false);
  const [healthDashboard, setHealthDashboard] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [showInformationalItems, setShowInformationalItems] = useState(false);
  const TAB_RECONCILE = 0;
  const TAB_HEALTH = 1;

  const summaryCards = useMemo(() => {
    if (!report) {
      return [];
    }

    return [
      {
        key: 'fetchedResults',
        label: t('moodleSync.metrics.fetchedResults', 'Fetched results'),
        value: report.fetchedResults,
      },
      { key: 'checkedPairs', label: t('moodleSync.metrics.checkedPairs', 'Checked pairs'), value: report.checkedPairs },
      {
        key: 'synchronizedCount',
        label: t('moodleSync.metrics.synchronizedCount', 'Synchronized'),
        value: report.synchronizedCount,
      },
      {
        key: 'nonSynchronizedCount',
        label: t('moodleSync.metrics.nonSynchronizedCount', 'Needs action'),
        value: report.nonSynchronizedCount,
      },
      {
        key: 'informationalCount',
        label: t('moodleSync.metrics.informationalCount', 'Informational'),
        value: report.informationalCount || 0,
      },
      { key: 'fixedCount', label: t('moodleSync.metrics.fixedCount', 'Fixed'), value: report.fixedCount },
      {
        key: 'unresolvedCount',
        label: t('moodleSync.metrics.unresolvedCount', 'Still unresolved'),
        value: report.unresolvedCount,
      },
    ];
  }, [report, t]);

  const buildRequestParams = () => {
    const fromDateParsed = parseNonNegativeInt(fromDateInput);
    if (fromDateParsed.error) {
      return { error: t('moodleSync.validation.fromDateNonNegative', 'From date must be a non-negative integer') };
    }

    const limitParsed = parseNonNegativeInt(limitInput);
    if (limitParsed.error) {
      return { error: t('moodleSync.validation.limitNonNegative', 'Limit must be a non-negative integer') };
    }

    return {
      params: {
        fromDate: fromDateParsed.value !== undefined ? fromDateParsed.value : 0,
        limit: limitParsed.value,
      },
    };
  };

  const fetchReport = async params => {
    const response = await getMoodleNonSynchronizedStatuses(params);

    if (isApiSuccess(response)) {
      return response;
    }

    setErrorMessage(getApiErrorMessage(response, t('errors.serverError', 'Server error')));
    return null;
  };

  const fetchHealthDashboard = async () => {
    setHealthLoading(true);
    try {
      const response = await getMoodleSyncHealthDashboard({ days: 14, top: 10 });
      if (isApiSuccess(response)) {
        setHealthDashboard(response);
      } else {
        setErrorMessage(getApiErrorMessage(response, t('errors.serverError', 'Server error')));
      }
    } catch {
      setErrorMessage(t('errors.serverError', 'Server error'));
    } finally {
      setHealthLoading(false);
    }
  };

  const runAudit = async () => {
    const builtParams = buildRequestParams();
    if (builtParams.error) {
      setErrorMessage(builtParams.error);
      return;
    }

    setLoading(true);
    setErrorMessage('');
    setLastAction('audit');

    try {
      const response = await fetchReport(builtParams.params);
      if (response) {
        setReport(response);
      }
    } catch {
      setErrorMessage(t('errors.serverError', 'Server error'));
    } finally {
      setLoading(false);
    }
  };

  const runReconciliation = async () => {
    const builtParams = buildRequestParams();
    if (builtParams.error) {
      setErrorMessage(builtParams.error);
      return;
    }

    const confirmed = window.confirm(t('moodleSync.confirmApplyReconcile', 'Run reconciliation with fixes now?'));
    if (!confirmed) {
      return;
    }

    setLoading(true);
    setErrorMessage('');
    setLastAction('reconcileFixAsync');

    try {
      const runResponse = await startMoodleReconcileRun({
        fromDate: builtParams.params.fromDate,
        limit: builtParams.params.limit,
        includeItems: false,
        applyFixes: true,
      });

      if (!isApiSuccess(runResponse)) {
        setErrorMessage(getApiErrorMessage(runResponse, t('errors.serverError', 'Server error')));
        return;
      }

      setReconcileRun(runResponse);
      setIsPollingRun(true);

      await new Promise(resolve => {
        const poll = async remainingAttempts => {
          const runStateResponse = await getMoodleReconcileRun(runResponse.id);
          if (!isApiSuccess(runStateResponse)) {
            setErrorMessage(getApiErrorMessage(runStateResponse, t('errors.serverError', 'Server error')));
            setIsPollingRun(false);
            resolve();
            return;
          }

          setReconcileRun(runStateResponse);
          if (runStateResponse.status === 'completed') {
            if (runStateResponse.report) {
              setReport(runStateResponse.report);
            }
            await fetchHealthDashboard();
            setIsPollingRun(false);
            resolve();
            return;
          }

          if (runStateResponse.status === 'failed') {
            setErrorMessage(
              runStateResponse.errorMessage || t('moodleSync.reconcileRunFailed', 'Reconciliation run failed'),
            );
            setIsPollingRun(false);
            resolve();
            return;
          }

          if (remainingAttempts <= 0) {
            setErrorMessage(t('moodleSync.reconcileRunTimeout', 'Reconciliation run is still in progress'));
            setIsPollingRun(false);
            resolve();
            return;
          }

          setTimeout(() => {
            poll(remainingAttempts - 1);
          }, 5000);
        };

        poll(720);
      });
    } catch {
      setErrorMessage(t('errors.serverError', 'Server error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === TAB_HEALTH && !healthDashboard && !healthLoading) {
      fetchHealthDashboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, healthDashboard, healthLoading]);

  const filteredItems = useMemo(() => {
    if (!report || !Array.isArray(report.items)) {
      return [];
    }

    let items = report.items;

    if (selectedReason) {
      items = items.filter(item => item.reason === selectedReason);
    }

    const selectedInformationalReason = selectedReason && isInformationalReason(selectedReason);
    if (!showInformationalItems && !selectedInformationalReason) {
      items = items.filter(item => !isInformationalReason(item.reason));
    }

    return items;
  }, [report, selectedReason, showInformationalItems]);

  return (
    <PageLayout pageTitle={t('moodleSync.pageTitle', 'Moodle Sync')} showLoader={isPollingRun}>
      <Paper className={classes.controlsWrap}>
        <Tabs
          value={activeTab}
          onChange={(_, tabValue) => setActiveTab(tabValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label={t('moodleSync.tabs.reconcile', 'Reconcile')} />
          <Tab label={t('moodleSync.tabs.health', 'Health dashboard')} />
        </Tabs>
      </Paper>

      {activeTab === TAB_RECONCILE ? (
        <>
          <Paper className={classes.controlsWrap}>
            <div className={classes.controlsSection}>
              <Grid container spacing={2} className={classes.controlsGrid}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label={t('moodleSync.fromDateLabel', 'From date (unix seconds)')}
                    value={fromDateInput}
                    onChange={event => setFromDateInput(event.target.value)}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label={t('moodleSync.limitLabel', 'Issue list limit')}
                    value={limitInput}
                    onChange={event => setLimitInput(event.target.value)}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <div className={classes.controlsButtons}>
                    <Button variant="contained" color="primary" onClick={runAudit} disabled={loading}>
                      {t('moodleSync.auditButton', 'Check sync issues')}
                    </Button>
                    <Button variant="contained" color="secondary" onClick={runReconciliation} disabled={loading}>
                      {t('moodleSync.applyButton', 'Reconcile and fix')}
                    </Button>
                  </div>
                </Grid>
              </Grid>
              <Divider className={classes.sectionDivider} />
              <div>
                <Typography variant="body2" className={classes.helperText}>
                  {t('moodleSync.lastAction', 'Last action')}: {t(`moodleSync.actions.${lastAction}`, lastAction)}
                </Typography>
                {reconcileRun ? (
                  <>
                    <Typography variant="body2" className={classes.helperText}>
                      {t('moodleSync.reconcileRunStatus', 'Reconcile run')} #{reconcileRun.id}: {reconcileRun.status}
                    </Typography>
                    {reconcileRun.report?.progress ? (
                      <>
                        <Typography variant="body2" className={classes.helperText}>
                          {t('moodleSync.reconcileRunProgress', 'Processed')}:{' '}
                          {reconcileRun.report.progress.processedResults} / {reconcileRun.report.progress.totalResults}{' '}
                          ({t('moodleSync.reconcileRunRemaining', 'remaining')}:{' '}
                          {reconcileRun.report.progress.remainingResults})
                        </Typography>
                        <Typography variant="body2" className={classes.helperText}>
                          {t('moodleSync.reconcileRunFixedSoFar', 'Fixed so far')}:{' '}
                          {reconcileRun.report.fixedCount || 0}
                        </Typography>
                      </>
                    ) : null}
                  </>
                ) : null}
                {errorMessage ? (
                  <Typography variant="body2" className={classes.errorText}>
                    {errorMessage}
                  </Typography>
                ) : null}
              </div>
            </div>
          </Paper>

          {report ? (
            <>
              <Paper className={classes.section}>
                <Typography variant="h6" className={classes.sectionHeader}>
                  {t('moodleSync.summaryTitle', 'Summary')}
                </Typography>
                <Grid container spacing={2} className={classes.metricsGrid}>
                  {summaryCards.map(card => (
                    <Grid item xs={12} sm={6} md={4} lg={2} key={card.key}>
                      <Paper className={classes.metricCard}>
                        <Typography variant="body2">{card.label}</Typography>
                        <Typography className={classes.metricValue}>{card.value}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
                <Divider className={classes.sectionDivider} />
                <Typography variant="subtitle1">{t('moodleSync.reasonsTitle', 'Issues by reason')}</Typography>
                <div className={classes.reasonsWrap}>
                  {REASON_ORDER.map(reason => {
                    const reasonCount =
                      report.issuesByReason && report.issuesByReason[reason] ? report.issuesByReason[reason] : 0;
                    const isInfoReason = isInformationalReason(reason);
                    return (
                      <Chip
                        key={reason}
                        className={`${classes.reasonChip} ${isInfoReason ? classes.reasonChipSecondary : ''}`}
                        label={`${t(`moodleSync.reasons.${reason}`, reason)}: ${reasonCount}`}
                        color={selectedReason === reason ? 'secondary' : 'default'}
                        variant={
                          selectedReason === reason || (!isInfoReason && reasonCount > 0) ? 'default' : 'outlined'
                        }
                        onClick={() => setSelectedReason(current => (current === reason ? '' : reason))}
                        clickable
                      />
                    );
                  })}
                </div>
                <Typography variant="body2" className={classes.filterHint}>
                  {t(
                    'moodleSync.reasonFilterHint',
                    'Click a reason chip to filter issue details. Click again to reset.',
                  )}
                </Typography>
                {report.truncated ? (
                  <Typography variant="body2" className={classes.helperText}>
                    {t('moodleSync.truncatedHint', 'Issue list is truncated by limit.')}
                  </Typography>
                ) : null}
              </Paper>

              <Paper className={classes.section}>
                <div className={classes.issuesHeader}>
                  <div>
                    <Typography variant="h6">{t('moodleSync.issuesTitle', 'Issue details')}</Typography>
                    {!showInformationalItems && !selectedReason ? (
                      <Typography variant="body2" className={classes.helperText}>
                        {t(
                          'moodleSync.infoHiddenByDefault',
                          'Informational items are hidden by default. Use the toggle or an informational reason chip to view them.',
                        )}
                      </Typography>
                    ) : null}
                  </div>
                  <div className={classes.issuesActions}>
                    <Button
                      size="small"
                      variant={showInformationalItems ? 'contained' : 'outlined'}
                      color={showInformationalItems ? 'secondary' : 'default'}
                      onClick={() => setShowInformationalItems(current => !current)}
                    >
                      {showInformationalItems
                        ? t('moodleSync.hideInformational', 'Hide informational')
                        : t('moodleSync.showInformational', 'Show informational')}
                    </Button>
                    {selectedReason ? (
                      <div className={classes.activeFilterWrap}>
                        <Typography variant="body2">
                          {t('moodleSync.filteredByReason', {
                            reason: t(`moodleSync.reasons.${selectedReason}`, selectedReason),
                            defaultValue: 'Filtered by reason: {{reason}}',
                          })}
                        </Typography>
                        <Button size="small" onClick={() => setSelectedReason('')}>
                          {t('moodleSync.clearReasonFilter', 'Clear filter')}
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </div>
                <Divider className={classes.sectionDivider} />
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('moodleSync.columns.reason', 'Reason')}</TableCell>
                        <TableCell>{t('moodleSync.columns.moodle', 'Moodle IDs')}</TableCell>
                        <TableCell>{t('moodleSync.columns.etrat', 'eTrat IDs')}</TableCell>
                        <TableCell>{t('moodleSync.columns.status', 'Status')}</TableCell>
                        <TableCell>{t('moodleSync.columns.states', 'Current vs expected')}</TableCell>
                        <TableCell>{t('moodleSync.columns.fix', 'Fix result')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredItems.length ? (
                        filteredItems.map((item, index) => (
                          <TableRow key={`${item.reason}-${index}`}>
                            <TableCell className={classes.tableCellCompact}>
                              {t(`moodleSync.reasons.${item.reason}`, item.reason)}
                            </TableCell>
                            <TableCell className={classes.tableCellCompact}>
                              <div>userId: {item.moodleUserId}</div>
                              <div>moodleCourseId: {item.moodleCourseId}</div>
                            </TableCell>
                            <TableCell className={classes.tableCellCompact}>
                              <div>userId: {item.etratUserId || '-'}</div>
                              <div>trainingSessionId: {item.etratCourseId || '-'}</div>
                            </TableCell>
                            <TableCell className={classes.tableCellCompact}>{item.status}</TableCell>
                            <TableCell className={classes.tableCellWrap}>
                              <div className={classes.stateLine}>current: {toDisplayState(item.currentState)}</div>
                              <div>expected: {toDisplayState(item.expectedState)}</div>
                              {item.reason === 'state_mismatch' ? (
                                <div>
                                  differences:{' '}
                                  {getStateMismatchFields(item.currentState, item.expectedState).length
                                    ? getStateMismatchFields(item.currentState, item.expectedState).join(', ')
                                    : 'none'}
                                </div>
                              ) : null}
                            </TableCell>
                            <TableCell className={classes.tableCellWrap}>
                              {INFO_ONLY_REASONS.includes(item.reason) ? (
                                <>
                                  <div>{t('moodleSync.info.noActionNeeded', 'No action needed')}</div>
                                  <div>{item.note || '-'}</div>
                                </>
                              ) : (
                                <>
                                  <div>fixed: {item.fixed ? 'yes' : 'no'}</div>
                                  <div>{item.fixError || item.note || '-'}</div>
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6}>
                            {t('moodleSync.noIssues', 'No issues found for selected filter.')}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          ) : null}
        </>
      ) : activeTab === TAB_HEALTH ? (
        <Paper className={classes.section}>
          <Grid container spacing={2} className={classes.controlsGrid}>
            <Grid item xs={12} md={8}>
              <Typography variant="h6">
                {t('moodleSync.health.title', 'Sync health dashboard (last 14 days)')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <div className={classes.controlsButtons}>
                <Button size="small" variant="outlined" onClick={fetchHealthDashboard} disabled={healthLoading}>
                  {t('moodleSync.health.refresh', 'Refresh health')}
                </Button>
              </div>
            </Grid>
          </Grid>
          {healthLoading ? (
            <Typography variant="body2" className={classes.helperText}>
              {t('moodleSync.health.loading', 'Loading health dashboard...')}
            </Typography>
          ) : null}
          {errorMessage ? (
            <Typography variant="body2" className={classes.errorText}>
              {errorMessage}
            </Typography>
          ) : null}
          {healthDashboard ? (
            <>
              <Typography variant="body2" className={classes.helperText}>
                {t('moodleSync.health.generatedAt', 'Generated at')}: {healthDashboard.generatedAt}
              </Typography>
              <Typography variant="body2" className={classes.helperText}>
                {t('moodleSync.health.runCounts', 'Runs')}: total {healthDashboard.runCounts.total}, completed{' '}
                {healthDashboard.runCounts.completed}, failed {healthDashboard.runCounts.failed}, in-progress{' '}
                {healthDashboard.runCounts.inProgress}, pending {healthDashboard.runCounts.pending}
              </Typography>
              <Typography variant="body2" className={classes.helperText}>
                {t('moodleSync.health.totals', 'Window totals')}: missing_issueid{' '}
                {healthDashboard.totals.missingIssueidCount}, missing_user_link{' '}
                {healthDashboard.totals.missingUserLinkCount}, fix_failed {healthDashboard.totals.fixFailedCount},
                fallback_failed {healthDashboard.totals.fallbackFailedCount}, unresolved{' '}
                {healthDashboard.totals.unresolvedCount}
              </Typography>

              <div className={classes.healthAlertWrap}>
                {(healthDashboard.alerts || []).length ? (
                  healthDashboard.alerts.map(alert => (
                    <Chip
                      key={`${alert.code}-${alert.message}`}
                      label={`${alert.severity.toUpperCase()}: ${alert.message}`}
                      className={
                        alert.severity === 'critical' ? classes.healthAlertCritical : classes.healthAlertWarning
                      }
                    />
                  ))
                ) : (
                  <Chip label={t('moodleSync.health.noAlerts', 'No active alerts')} />
                )}
              </div>

              <TableContainer className={classes.healthTable}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('moodleSync.health.columns.date', 'Date')}</TableCell>
                      <TableCell>{t('moodleSync.health.columns.runs', 'Runs')}</TableCell>
                      <TableCell>{t('moodleSync.health.columns.missingIssueid', 'missing_issueid')}</TableCell>
                      <TableCell>{t('moodleSync.health.columns.missingUserLink', 'missing_user_link')}</TableCell>
                      <TableCell>{t('moodleSync.health.columns.fixFailed', 'fix_failed')}</TableCell>
                      <TableCell>{t('moodleSync.health.columns.fallback', 'fallback')}</TableCell>
                      <TableCell>{t('moodleSync.health.columns.unresolved', 'unresolved')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(healthDashboard.dailyTrend || []).map(point => (
                      <TableRow key={point.date}>
                        <TableCell>{point.date}</TableCell>
                        <TableCell>
                          {point.runsStarted} (ok {point.runsCompleted}, failed {point.runsFailed})
                        </TableCell>
                        <TableCell>{point.missingIssueidCount}</TableCell>
                        <TableCell>{point.missingUserLinkCount}</TableCell>
                        <TableCell>{point.fixFailedCount}</TableCell>
                        <TableCell>
                          attempted {point.fallbackAttemptedCount}, failed {point.fallbackFailedCount}
                        </TableCell>
                        <TableCell>{point.unresolvedCount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Grid container spacing={2} className={classes.healthTable}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1">{t('moodleSync.health.topUsers', 'Top affected users')}</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Moodle user</TableCell>
                        <TableCell>eTrat user</TableCell>
                        <TableCell>Issues</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(healthDashboard.topAffectedUsers || []).length ? (
                        healthDashboard.topAffectedUsers.map(item => (
                          <TableRow key={`${item.moodleUserId}-${item.etratUserId || 0}`}>
                            <TableCell>{item.moodleUserId}</TableCell>
                            <TableCell>{item.etratUserId || '-'}</TableCell>
                            <TableCell>{item.issueCount}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3}>{t('moodleSync.health.noTopUsers', 'No user impact data')}</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1">
                    {t('moodleSync.health.topCourses', 'Top affected courses')}
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Moodle course</TableCell>
                        <TableCell>eTrat course</TableCell>
                        <TableCell>Issues</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(healthDashboard.topAffectedCourses || []).length ? (
                        healthDashboard.topAffectedCourses.map(item => (
                          <TableRow key={`${item.moodleCourseId}-${item.etratCourseId || 0}`}>
                            <TableCell>{item.moodleCourseId}</TableCell>
                            <TableCell>{item.etratCourseId || '-'}</TableCell>
                            <TableCell>{item.issueCount}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3}>
                            {t('moodleSync.health.noTopCourses', 'No course impact data')}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Grid>
              </Grid>
            </>
          ) : null}
        </Paper>
      ) : null}
    </PageLayout>
  );
};

export default MoodleSync;
